import { Card, Tag } from "pepsico-ds";
import ReactPlayer from "react-player";
import ImagePreview from "../common/ImagePreview";
import JsonViewer from "../common/JsonViewer";
import "./performanceDetails.scss";

const PerformanceDetailCard = ({ label, value, type }) => {
  const shouldRenderTag = ["status", "source", "tags"].includes(label.toLowerCase());
  const shouldRenderImage =
    label.toLowerCase().includes("image") || type === "image";
  const shouldRenderVideo = type === "video";
  const shouldRenderAudio = type === "audio";
  const shouldRenderSurvey = type === "survey";

  const isArrayValue = Array.isArray(value);

  const getTagColor = (val) => {
    const colorMap = {
      approved: "Green-150",
      pending: "Yellow-150",
      rejected: "Red-150",
      failed: "Red-400",
    };

    return colorMap[val] || "Gray-100";
  };

  const renderTags = () => {
    if (isArrayValue) {
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {value.map((val, index) => (
            <Tag
              key={`${label}-${index}`}
              text={val}
              color={getTagColor(val?.toLowerCase())}
              isCopyable={false}
            />
          ))}
        </div>
      );
    }

    return (
      <Tag
        text={value}
        color={getTagColor(value?.toLowerCase())}
        isCopyable={false}
      />
    );
  };

  const renderUnavailableMessage = (message) => (
    <h5 style={{ fontWeight: "normal" }}>{message}</h5>
  );

  const renderMediaContent = (mediaType) => {
    if (!value || value === "-") {
      return renderUnavailableMessage(`No ${mediaType} available`);
    }

    return (
      <>
        <div className={`${mediaType}-container`}>
          <ReactPlayer src={value} controls />
        </div>
        <div className="media-url-container">
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="media-url-link"
          >
            {value}
          </a>
        </div>
      </>
    );
  };

  const renderSurveyTable = (fields) => (
    <table className="survey-questions-table">
      <thead>
        <tr>
          <th>Questions</th>
          <th>Answers</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((field, index) => (
          <tr key={`${field.key}-${index}`}>
            <td>{field.key || "-"}</td>
            <td>
              <span className="survey-answer-value">{field.value || "-"}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderImageContent = () => {
    if (!value) {
      return renderUnavailableMessage("No image available");
    }

    const imageSrc =
      value.startsWith("data:image") ||
      value.startsWith("http://") ||
      value.startsWith("https://")
        ? value
        : `data:image/jpeg;base64,${value}`;

    const isUrl = value.startsWith("http://") || value.startsWith("https://");

    return (
      <>
        <ImagePreview imageSrc={imageSrc} alt={label} />
        {isUrl && (
          <div className="media-url-container">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="media-url-link"
            >
              {value}
            </a>
          </div>
        )}
      </>
    );
  };

  const renderOutcomes = () => (
    <div>
      <ul>
        {value.map((outcome, index) => (
          <li key={`${outcome.name}-${outcome.value}-${index}`}>
            <h5 style={{ fontWeight: "normal" }}>
              {outcome.name}: {outcome.value}
            </h5>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderDefaultContent = () => (
    <h5 style={{ fontWeight: "normal" }}>
      {isArrayValue ? value.join(", ") : value}
    </h5>
  );

  const renderContent = () => {
    if (shouldRenderVideo) {
      return renderMediaContent("video");
    }

    if (shouldRenderAudio) {
      return renderMediaContent("audio");
    }

    if (shouldRenderSurvey && Array.isArray(value)) {
      return renderSurveyTable(value);
    }

    if (shouldRenderImage) {
      return renderImageContent();
    }

    if (shouldRenderTag) {
      return renderTags();
    }

    if (label.toLowerCase() === "json") {
      return <JsonViewer data={value} />;
    }

    if (isArrayValue && label === "Outcomes") {
      return renderOutcomes();
    }

    if (isArrayValue && label === "Survey Questions") {
      return renderSurveyTable(value);
    }

    return renderDefaultContent();
  };

  const shouldHideLabel = shouldRenderImage && !type;
  const shouldUsePadding = !shouldRenderImage || type === "image";

  return (
    <Card
      border
      style={{
        backgroundColor: "#EFF5FC",
        padding: shouldUsePadding ? "15px" : "0px",
      }}
    >
      {!shouldHideLabel && <h5>{label}</h5>}
      {renderContent()}
    </Card>
  );
};

export default PerformanceDetailCard;
